import { Dialog, Disclosure, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@lxp-app/store/store";
import { VisibilityGuard } from "@lxp-app/utils/visibilityGuard";
import { AppMetaDataSliceActions } from "../../store/slices/appMetaDataSlice";
import { useLazyGetCommonConfigQuery } from "@lxp-app/store/slices/api/commonConfigApi";
import { getAuthoringLibraryLabel } from "@lxp-app/utils/authoringLibraryResources/localizeAuthoringLibraryLabels";
const SurveyPanel = React.lazy(
  () => import("../../components/atoms/survey-panel/survey-panel")
);
const AUTHORING_ROUTES = [
  "authoring",
  "learningpath-authoring",
  "content-authoring",
  "event-authoring",
  "assessment-authoring",
  "question-paper-authoring",
  "question-authoring",
  "course-authoring"
];

export default function Footer() {
  const location = useLocation();
  const isAuthoringPage = (() => {
    const parts = location.pathname.split("/").filter(Boolean);

    const lastSegment = parts[parts.length - 1];

    return AUTHORING_ROUTES.includes(lastSegment);
  })();

  const user = useSelector((state: RootState) => state.UserInfoSlice);
  const language = user?.profile?.preferences?.language || "en";
  const hasAssessmentRecOrgs = user?.profile?.hasAssessmentRecOrgs;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleTakeSurvey = () => {
    const surveyId = import.meta.env.VITE_PLATFORM_FEEDBACK_SURVEY_ID;
    navigate(`/content-player/survey/${surveyId}`);
  };
  const [surveyParentId, setSurveyParentId] = useState("");
  const [surveyId, setSurveyId] = useState("");
  const [fetchSurveyId, setFetchSurveyId] = useState(false);
  const [getSurveyId] = useLazyGetCommonConfigQuery();

  useEffect(() => {
    if (!surveyId && fetchSurveyId) {
      getSurveyId("platformFeedbackIdParentId").then((res: any) => {
        setSurveyId(res.data.data.value.survey);
        setSurveyParentId(res.data.data.value.parentId);
      });
    }
  }, [surveyId, fetchSurveyId]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (open) {
      dispatch(AppMetaDataSliceActions.setFeedbackModalOpen(true));
    } else {
      timeoutRef.current = setTimeout(() => {
        dispatch(AppMetaDataSliceActions.setFeedbackModalOpen(false));
      }, 750);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [open, dispatch]);

  return (
    <>
      <Disclosure
        as="nav"
        className={
          isAuthoringPage
            ? "hidden sm:block bg-zinc-200 dark:bg-zinc-800 fixed sm:bottom-0 left-0 right-0 z-40 shadow"
            : "bg-zinc-200 dark:bg-zinc-800 relative shadow bottom-0"
        }
      >
        {({}) => (
          <>
            <div className="px-1 sm:px-5 md:px-9 xl:px-16 py-1">
              <div className="flex flex-col md:flex-row text-sm items-center h-auto md:h-10 justify-between">
                <div className="flex items-center py-3 md:py-0 flex-row md:items-start px-5 sm:px-0">
                  <div className="sm:text-sm text-xs">
                    &#169; {new Date().getFullYear()} GlobalLogic Inc
                  </div>
                </div>
                {hasAssessmentRecOrgs && (
                  <div className="flex flex-wrap justify-center font-medium text-gray-800 dark:text-gray-200 text-base gap-2 sm:gap-4 h-auto">
                    <Link
                      to={
                        "https://mail.google.com/mail/?view=cm&fs=1&to=support.glx@globallogic.com"
                      }
                      target="_blank"
                      className="visited:text-gray-700 dark:visited:text-white"
                    >
                      {getAuthoringLibraryLabel("platformHelp", language)}
                    </Link>
                    <div className="border-r-[1px] h-auto border-gray-300 dark:border-gray-600"></div>
                    <Link
                      to={"https://www.globallogic.com/privacy-policy/"}
                      target="_blank"
                      className="visited:text-gray-700 dark:visited:text-white"
                    >
                      {getAuthoringLibraryLabel("privacyPolicy", language)}
                    </Link>
                  </div>
                )}
                <VisibilityGuard
                  NotVisibleCondition={
                    hasAssessmentRecOrgs || location.pathname == "/tos"
                  }
                >
                  <div className="flex items-center flex-row  px-5 sm:px-0 md:pb-0 pb-3">
                    {/* <Link
                    to="/tos"
                    className="text-sm md:text-md hover:text-primary"
                    data-testid="tosLink"
                  >
                    Platform Help
                  </Link>
                  <div className="px-2">|</div> */}

                    <div className="flex flex-wrap justify-center font-medium text-gray-800 dark:text-gray-200 text-base gap-2 sm:gap-4">
                      {/* <Link to="/meet-our-team" data-testid="aboutusLink">
                        {" "}
                        Meet our Team
                      </Link>
                      <div className="border-r-[1px] h-auto border-gray-300 dark:border-gray-600"></div> */}
                      <Link
                        to="https://globallogic.atlassian.net/servicedesk/customer/portal/30/group/344"
                        target="_blank"
                      >
                        Raise a Request
                      </Link>
                      <div className="border-r-[1px] h-auto border-gray-300 dark:border-gray-600"></div>
                      <button
                        onClick={() => {
                          setOpen(true);
                          setFetchSurveyId(true);
                        }}
                        data-testid="platformFeedbackLink"
                      >
                        Feedback
                      </button>
                    </div>
                  </div>
                </VisibilityGuard>
              </div>
            </div>
          </>
        )}
      </Disclosure>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="fixed inset-0 z-[1000]" onClose={setOpen}>
          <SurveyPanel
            surveyId={surveyId}
            parentId={surveyParentId}
            setOpen={setOpen}
          />
        </Dialog>
      </Transition.Root>
    </>
  );
}
